import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import API from '../services/api';

// Helper function to safely get signature data regardless of field names
function getSignatureData(form, type) {
  if (!form) return '';
  
  // Check for different possible signature field names based on form structure
  let sigData = '';
  
  if (type === 'consent') {
    // Try all possible field names for consent signature
    sigData = form.consent_signature || 
              form.consentSignatureData || 
              (form.data_json && form.data_json.consentSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'cancel') {
    // Try all possible field names for cancellation signature
    sigData = form.cancel_signature || 
              form.cancelSignatureData || 
              (form.data_json && form.data_json.cancelSignatureData) || 
              form.signature || 
              '';
  } else if (type === 'final') {
    // Try all possible field names for final signature
    sigData = form.final_signature || 
              form.finalSignatureData || 
              (form.data_json && form.data_json.finalSignatureData) || 
              form.signature || 
              '';
  }
  
  // Debug logging
  console.log(`Signature type: ${type}`, sigData ? 'Found (length: ' + sigData.length + ')' : 'Missing');
  
  return sigData;
}

function AdminClientProfilePage() {
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // Local DB intake forms
  const [localForms, setLocalForms] = useState([]);
  
  // View states
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFormPreview, setShowFormPreview] = useState(false);

  // Manual paste
  const [manualData, setManualData] = useState('');
  const [pasteMessage, setPasteMessage] = useState('');

  // Acuity clients (but no appointment data by default)
  const [acuityClients, setAcuityClients] = useState([]);
  const [acuitySearch, setAcuitySearch] = useState('');
  const [showAcuityList, setShowAcuityList] = useState(false);

  // Acuity forms data by email
  const [acuityFormsByEmail, setAcuityFormsByEmail] = useState([]);

  // Track whether each popup (modal) is open
  const [showAcuityModal, setShowAcuityModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);

  useEffect(() => {
    fetchAllPortalClients();
  }, []);

  // -----------------------------------------------------
  // HELPER FUNCTIONS
  // -----------------------------------------------------

  /** Convert Acuity "forms" arrays into an array of { label,value }. */
  function parseAcuityFormAnswers(formAnswers) {
    if (!Array.isArray(formAnswers)) return [];
    const results = [];
    formAnswers.forEach((oneForm) => {
      if (Array.isArray(oneForm.values)) {
        oneForm.values.forEach((v) => {
          results.push({
            label: v.name || 'N/A',
            value: v.value || ''
          });
        });
      }
    });
    return results;
  }

  /** Store selected fields from an Acuity appointment in the DB. */
  async function handleStoreInDB(acuityAppointment) {
    if (!selectedClient) {
      alert('Please select a local client before storing data in DB.');
      return;
    }
    const parsed = parseAcuityFormAnswers(acuityAppointment.form_answers || []);
    try {
      const res = await API.post('/forms/admin/store-acuity-fields', {
        clientId: selectedClient.id,
        formAnswers: parsed,
        overrideEmail: acuityAppointment.email,
        overridePhone: acuityAppointment.phone
      });
      if (res.data.success) {
        alert('Successfully stored selected fields in DB!');

        // 1) refresh local intake records
        const formsRes = await API.get(
          `/forms/admin/intake-data/${selectedClient.id}`
        );
        setLocalForms(formsRes.data || []);

        // 2) refresh the client details (phone/email etc.)
        const detailRes = await API.get(`/admin/clients/${selectedClient.id}`);
        setSelectedClient(detailRes.data);
      } else {
        alert(`Error storing fields: ${res.data.error || 'unknown'}`);
      }
    } catch (error) {
      console.error('Error storing fields in DB:', error);
      alert('Server error storing fields.');
    }
  }

  /** Detect form type from data */
  function determineFormType(formData) {
    const dataJson = formData.data_json || {};
    
    if (dataJson.consentDate && dataJson.clientName) {
      return "Intake Form";
    }
    
    if (dataJson.storedFields && Array.isArray(dataJson.storedFields)) {
      return "Acuity Intake";
    }
    
    if (dataJson.basicInfo) {
      return "Manual Intake";
    }
    
    return "Client Form";
  }

  /** Format date for display */
  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /** Pretty-print Acuity answers, with optional DB-store button. */
  function renderFormAnswers(formAnswers, appt = null) {
    const parsed = parseAcuityFormAnswers(formAnswers);
    if (parsed.length === 0) return null;

    return (
      <div style={{ marginTop: '0.5rem' }}>
        <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
          {parsed.map((qa, i) => (
            <div key={i} style={{ marginBottom: '0.5rem' }}>
              <strong>{qa.label}:</strong> {qa.value}
            </div>
          ))}
        </div>
        {selectedClient && appt && (
          <button 
            onClick={() => handleStoreInDB(appt)}
            className="btn btn-primary btn-sm"
          >
            Store Key Fields in DB
          </button>
        )}
      </div>
    );
  }

  // -----------------------------------------------------
  // PDF GENERATION
  // -----------------------------------------------------
  const downloadFormAsPdf = (formData) => {
    if (!formData) return;
    
    // Log the entire form data to debug
    console.log('Full form data for PDF generation:', formData);
    
    const recordData = formData.data_json || {};
    const formType = determineFormType(formData);
    
    // Create a new window for printing with scrollbars enabled
    const newWin = window.open('', '_blank', 'width=800,height=900,scrollbars=yes');
    if (!newWin) {
      alert('Failed to open new window for PDF export. Please check your popup blocker settings.');
      return;
    }

    // Extract signature data from the form using our helper function
    const consentSignatureData = getSignatureData(formData, 'consent');
    const cancelSignatureData = getSignatureData(formData, 'cancel');
    const finalSignatureData = getSignatureData(formData, 'final');
    
    // Debug signature data
    console.log('Form data:', formData);
    console.log('Signatures:', {
      consent: consentSignatureData ? 'Present (length: ' + consentSignatureData.length + ')' : 'Missing',
      cancel: cancelSignatureData ? 'Present (length: ' + cancelSignatureData.length + ')' : 'Missing',
      final: finalSignatureData ? 'Present (length: ' + finalSignatureData.length + ')' : 'Missing'
    });
    
    // If data_json contains signature data directly, try to use it
    if (recordData.consentSignatureData && !consentSignatureData) {
      console.log('Using signature from data_json.consentSignatureData');
    }
    if (recordData.cancelSignatureData && !cancelSignatureData) {
      console.log('Using signature from data_json.cancelSignatureData');
    }
    if (recordData.finalSignatureData && !finalSignatureData) {
      console.log('Using signature from data_json.finalSignatureData');
    }
    
    // Client info from form or client record
    const clientName = recordData.clientName || selectedClient.client_name;
    const patientNames = recordData.patientNames || '';
    const diagnosisCode = recordData.diagnosisCode || '';
    const clientPhone = recordData.clientPhone || selectedClient.phone || '';
    const clientEmail = recordData.clientEmail || selectedClient.email || '';
    const billingAddress = recordData.billingAddress || selectedClient.billing_address || '';
    const billingCity = recordData.billingCity || selectedClient.city || '';
    const billingState = recordData.billingState || selectedClient.state || '';
    const billingZipCode = recordData.billingZipCode || selectedClient.zip_code || '';
    
    // Dates and consent info
    const consentDate = recordData.consentDate ? formatDate(recordData.consentDate) : formatDate(formData.created_at);
    const cancelDate = recordData.cancelDate ? formatDate(recordData.cancelDate) : formatDate(formData.created_at);
    const initials = recordData.initials || '';
    
    // Build the new window's HTML - use the same layout as in ClientIntakePage
    newWin.document.write(`
      <html>
      <head>
        <title>Avenues Intake Form</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 20px;
            padding: 20px;
            line-height: 1.5;
          }
          .page {
            border: 1px solid #000;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            position: relative;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
          }
          .signature {
            width: 200px;
            height: auto;
          }
          h2, h3 {
            text-align: center;
            margin-bottom: 20px;
          }
          .section {
            margin-top: 40px;
          }
          .date-line {
            display: inline-block;
            margin-left: 40px;
          }
          .client-info {
            margin-top: 20px;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .subsection {
            margin-top: 30px;
          }
          .indent {
            margin-left: 20px;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- Header Image -->
          <div class="header">
            <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
          </div>
          
          <h2>CONSENT TO TREAT</h2>
          <p>
            I, ${clientName}, give my permission and consent to Avenues Unlimited Counseling Center,
            Inc., to provide me psychotherapeutic treatment to ${patientNames || 'myself'}.
          </p>
          <p>
            While Avenues Unlimited will endeavor to provide standard of care treatment, I fully understand that 
            because of factors beyond Avenues Unlimited's control, particular outcomes cannot be guaranteed. 
            Furthermore, I understand that I/he/she/we may experience emotional strains because of the counseling 
            or therapy, feel worse during the treatment and make life changes which could be difficult.
          </p>
          <p>
            I understand that Avenues Unlimited is not providing emergency services, and I have been informed of whom to call upon in an emergency
            or during such time as treatment from Avenues Unlimited is unavailable.
          </p>
          <p>
            I understand that regular attendance as recommended by Avenues Unlimited will facilitate maximum therapeutic benefits, but that I/we
            am/are free to discontinue treatment at any time. If I decide to do so I will notify Avenues Unlimited
            at least two weeks in advance so that effective planning for continuing care can be implemented.
          </p>

          <div>
            <p>Consent Signature:</p>
            <img src="${consentSignatureData || (recordData.consentSignatureData ? recordData.consentSignatureData : '')}" class="signature" alt="Consent Signature">
            <span class="date-line">Date: ${consentDate}</span>
          </div>

          <div class="section">
            <h3>CANCELLATION POLICY</h3>
            <p>
              I understand that there is a 24 hour (or 1 working day) rescheduling policy. 
              A rescheduling charge of the full session fee will result if I do not give notice in this time frame.
            </p>
            
            <p>Cancellation Signature:</p>
            <img src="${cancelSignatureData || (recordData.cancelSignatureData ? recordData.cancelSignatureData : '')}" class="signature" alt="Cancellation Signature">
            <span class="date-line">Date: ${cancelDate}</span>
          </div>

          <div class="section">
            <h3>LIMITS OF CONFIDENTIALITY</h3>
            <p>
              The contents of counseling, intake and/or assessment sessions are confidential. 
              Both verbal information and written records about a client cannot be shared with another party without 
              the written consent of the client or the client's legal guardian, except as required by law. 
              It is the policy of this center not to release any information about a client without a signed 
              release of information, or as otherwise required by law. Noted exceptions are as follows:
            </p>
            
            <div class="subsection">
              <h4>DUTY TO WARN AND PROTECT</h4>
              <p class="indent">
                When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law 
                to warn the intended victim and report identifying information to legal authorities. In cases in which 
                the client discloses or implies a plan for suicide, Avenues Unlimited is required to notify legal 
                authorities and make reasonable attempts to notify the family of the client.
              </p>
            </div>
            
            <div class="subsection">
              <h4>ABUSE OF CHILDREN AND VULNERABLE ADULTS</h4>
              <p class="indent">
                If a client discloses or suggests that he or she is abusing a child (or vulnerable adult) or has recently 
                abused a child (or vulnerable adult); or a child (or vulnerable adult) is in danger of abuse, 
                Avenues Unlimited is required to report this information to the appropriate social service and/or legal authorities.
              </p>
            </div>
            
            <div class="subsection">
              <h4>PRENATAL EXPOSURE TO CONTROLLED SUBSTANCES</h4>
              <p class="indent">
                Avenues Unlimited is required by law to report admitted prenatal exposure to controlled substances 
                that are potentially harmful.
              </p>
            </div>
            
            <div class="subsection">
              <h4>IN THE EVENT OF A CLIENT'S DEATH</h4>
              <p class="indent">
                In the event of a client's death, the spouse or parents of a deceased client have a right to access a client's treatment records.
              </p>
            </div>
            
            <div class="subsection">
              <h4>PROFESSIONAL MISCONDUCT</h4>
              <p class="indent">
                Professional misconduct by a health care professional must be reported by other health care professionals. 
                In cases in which a professional or legal disciplinary meeting is being held regarding the health care professional's actions, 
                related records may be released in order to substantiate disciplinary concerns.
              </p>
            </div>
            
            <div class="subsection">
              <h4>COURT ORDERS</h4>
              <p class="indent">
                Health care professionals are required to release records of clients when a court order has been entered.
              </p>
            </div>
            
            <div class="subsection">
              <h4>MINORS/GUARDIANSHIP</h4>
              <p class="indent">
                Parents or legal guardians of non-emancipated minor clients have the right to access the client's records.
              </p>
            </div>
            
            <p>
              I have been informed of the limits of confidentiality. Initials: <strong>${initials}</strong>
            </p>
          </div>
          
          <div class="section">
            <h3>OTHER PROVISIONS</h3>
            <p>
              Payment for services is due and payable at the time services are rendered by Avenues Unlimited. 
              In the event that you fail to pay for any such services when rendered, Avenues Unlimited reserves the right 
              to turn delinquent accounts over to collection agencies and/or attorneys without prior notice. 
              Unpaid account balances accrue interest at the rate of 1.5% per month until paid. Further, in the event 
              Avenues Unlimited incurs attorney's fees and/or court costs to collect delinquent account balances, 
              client agrees to pay or reimburse Avenues Unlimited for the same. Avenues Unlimited may report delinquent 
              account balances to credit reporting agencies, and the client's credit report may state the amount owed, 
              time frame and the name of the clinic.
            </p>
            
            <p>
              Avenues Unlimited may furnish insurance companies and other third-party payers information regarding 
              services rendered by Avenues Unlimited to clients. Information which may be furnished by Avenues Unlimited 
              to these parties includes a description of the type of services, date/times of services, diagnosis, 
              treatment plan, description of impairment, progress of therapy, case notes and summaries. 
              Please note that Avenues Unlimited does not accept insurance payments unless otherwise agreed to in 
              writing between Avenues Unlimited and client.
            </p>
            
            <p>
              When couples, groups or families are receiving services, separate files are kept for individuals for 
              information disclosed that is of a confidential nature. This information includes (a) testing results, 
              (b) information given to the mental health professional not in the presence of the other person(s) 
              utilizing services, (c) information received from other sources about the client, (d) diagnosis, 
              (e) treatment plan, (f) individual reports/summaries and (h) information that has been requested to be separate. 
              The material disclosed in conjoint family or couples sessions in which each part discloses such information 
              in each other's presences, is kept in each file in the form of case notes.
            </p>
            
            <p>
              In the event Avenues Unlimited must telephone or e-mail the client for purposes such as appointments, 
              cancellations or reminders or to give or receive other information, efforts are made to preserve confidentiality.
              
              <br><br>
              Phone Contact: ${clientPhone || 'Not provided'}
              <br>
              Email Contact: ${clientEmail || 'Not provided'}
            </p>
          </div>

          <div class="section">
            <h3>ACKNOWLEDGMENT</h3>
            <p>
              By signing below, I acknowledge that I have read, understood, and agree to all the terms 
              and conditions outlined in this consent form. I have been given the opportunity to ask questions
              about the content of this form, and I understand my rights and responsibilities as a client at
              Avenues Unlimited Counseling Center.
            </p>
            
            <p>Signature:</p>
            <img src="${finalSignatureData || (recordData.finalSignatureData ? recordData.finalSignatureData : '')}" class="signature" alt="Final Acknowledgment Signature">
            <span class="date-line">Date: ${consentDate}</span>
          </div>

          <div class="client-info">
            <h3>Client Information</h3>
            ${diagnosisCode ? `<p><strong>Diagnosis Code:</strong> ${diagnosisCode}</p>` : ''}
            ${clientPhone ? `<p><strong>Phone:</strong> ${clientPhone}</p>` : ''}
            ${clientEmail ? `<p><strong>Email:</strong> ${clientEmail}</p>` : ''}
            ${billingAddress || billingCity || billingState || billingZipCode ? 
              `<p><strong>Address:</strong> ${billingAddress}${billingAddress && (billingCity || billingState || billingZipCode) ? ', ' : ''}
              ${billingCity}${billingCity && (billingState || billingZipCode) ? ', ' : ''}
              ${billingState} ${billingZipCode}</p>` : ''}
          </div>

          <!-- Footer Image -->
          <div class="footer">
            <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
          </div>
        </div>

        <script>
          window.onload = function() {
            window.focus();
            window.print();
            // Don't close automatically - let user close
          };
        </script>
      </body>
      </html>
    `);
    
    newWin.document.close();
  };

  function renderFormDataAsHtml(formData) {
    const recordData = formData.data_json || {};
    let html = '';
    
    // For Acuity intake
    if (Array.isArray(recordData.storedFields)) {
      html += '<div>';
      recordData.storedFields.forEach((item, idx) => {
        html += `<div class="field">
          <span class="field-label">${item.label}:</span> 
          <span>${item.value}</span>
        </div>`;
      });
      html += '</div>';
      return html;
    }
    
    // For manual intake
    if (recordData.basicInfo) {
      html += '<h3>Basic Info</h3>';
      html += `<div class="field"><span class="field-label">Name:</span> <span>${recordData.basicInfo.name || ''}</span></div>`;
      html += `<div class="field"><span class="field-label">Phone:</span> <span>${recordData.basicInfo.phone || ''}</span></div>`;
      html += `<div class="field"><span class="field-label">Email:</span> <span>${recordData.basicInfo.email || ''}</span></div>`;

      if (recordData.priceAndLocation) {
        html += '<h3>Price &amp; Location</h3>';
        html += `<div class="field"><span class="field-label">Price:</span> <span>${recordData.priceAndLocation.price || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">Location:</span> <span>${recordData.priceAndLocation.location || ''}</span></div>`;
      }

      if (recordData.contactPersonal) {
        html += '<h3>Contact / Personal</h3>';
        html += `<div class="field"><span class="field-label">Guardian Name:</span> <span>${recordData.contactPersonal.guardianName || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">Gender:</span> <span>${recordData.contactPersonal.gender || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">DOB:</span> <span>${recordData.contactPersonal.dob || ''}</span></div>`;
      }
      
      return html;
    }
    
    // For client intake form
    if (recordData.clientName) {
      html += '<h3>Client Information</h3>';
      html += `<div class="field"><span class="field-label">Name:</span> <span>${recordData.clientName || ''}</span></div>`;
      
      if (recordData.patientNames) {
        html += `<div class="field"><span class="field-label">Patient Names:</span> <span>${recordData.patientNames || ''}</span></div>`;
      }
      
      if (recordData.diagnosisCode) {
        html += `<div class="field"><span class="field-label">Diagnosis Code:</span> <span>${recordData.diagnosisCode || ''}</span></div>`;
      }
      
      if (recordData.clientPhone || recordData.clientEmail) {
        html += '<h3>Contact Information</h3>';
        if (recordData.clientPhone) {
          html += `<div class="field"><span class="field-label">Phone:</span> <span>${recordData.clientPhone || ''}</span></div>`;
        }
        if (recordData.clientEmail) {
          html += `<div class="field"><span class="field-label">Email:</span> <span>${recordData.clientEmail || ''}</span></div>`;
        }
      }
      
      if (recordData.billingAddress || recordData.billingCity || recordData.billingState || recordData.billingZipCode) {
        html += '<h3>Billing Address</h3>';
        html += `<div class="field"><span class="field-label">Address:</span> <span>${recordData.billingAddress || ''}</span></div>`;
        
        let cityStateZip = '';
        if (recordData.billingCity) cityStateZip += recordData.billingCity;
        if (recordData.billingCity && (recordData.billingState || recordData.billingZipCode)) cityStateZip += ', ';
        if (recordData.billingState) cityStateZip += recordData.billingState;
        if (recordData.billingState && recordData.billingZipCode) cityStateZip += ' ';
        if (recordData.billingZipCode) cityStateZip += recordData.billingZipCode;
        
        if (cityStateZip) {
          html += `<div class="field"><span class="field-label">City, State, Zip:</span> <span>${cityStateZip}</span></div>`;
        }
      }
      
      // Show if signatures are present
      html += '<h3>Signatures</h3>';
      
      // Check for signature data in multiple locations
      const hasConsentSig = recordData.consentSignatureData || recordData.consent_signature ? 'Present' : 'Missing';
      const hasCancelSig = recordData.cancelSignatureData || recordData.cancel_signature ? 'Present' : 'Missing';
      const hasFinalSig = recordData.finalSignatureData || recordData.final_signature ? 'Present' : 'Missing';
      
      html += `<div class="field"><span class="field-label">Consent Signature:</span> <span>${hasConsentSig}</span></div>`;
      html += `<div class="field"><span class="field-label">Cancellation Signature:</span> <span>${hasCancelSig}</span></div>`;
      html += `<div class="field"><span class="field-label">Final Signature:</span> <span>${hasFinalSig}</span></div>`;
      
      if (recordData.initials) {
        html += `<div class="field"><span class="field-label">Confidentiality Initials:</span> <span>${recordData.initials}</span></div>`;
      }
      
      return html;
    }
    
    // Default: just stringify the JSON
    return `<pre>${JSON.stringify(recordData, null, 2)}</pre>`;
  }
  
  // -----------------------------------------------------
  // FORM PREVIEW MODAL
  // -----------------------------------------------------
  function renderFormPreview() {
    if (!selectedForm) return null;
    
    // Debug logging for form data
    console.log('Selected form data:', selectedForm);
    console.log('Data JSON:', selectedForm.data_json);
    
    const recordData = selectedForm.data_json || {};
    const formType = determineFormType(selectedForm);
    
    // Extract client info from form data or client record
    const clientName = recordData.clientName || (selectedClient ? selectedClient.client_name : '');
    const patientNames = recordData.patientNames || '';
    const diagnosisCode = recordData.diagnosisCode || '';
    const clientPhone = recordData.clientPhone || (selectedClient ? selectedClient.phone || '' : '');
    const clientEmail = recordData.clientEmail || (selectedClient ? selectedClient.email || '' : '');
    const billingAddress = recordData.billingAddress || (selectedClient ? selectedClient.billing_address || '' : '');
    const billingCity = recordData.billingCity || (selectedClient ? selectedClient.city || '' : '');
    const billingState = recordData.billingState || (selectedClient ? selectedClient.state || '' : '');
    const billingZipCode = recordData.billingZipCode || (selectedClient ? selectedClient.zip_code || '' : '');
    
    // Extract dates and consent info
    const consentDate = recordData.consentDate ? formatDate(recordData.consentDate) : formatDate(selectedForm.created_at);
    const cancelDate = recordData.cancelDate ? formatDate(recordData.cancelDate) : formatDate(selectedForm.created_at);
    const initials = recordData.initials || '';
    
    // Get signature data using our helper function
    const consentSignatureData = getSignatureData(selectedForm, 'consent');
    const cancelSignatureData = getSignatureData(selectedForm, 'cancel');
    const finalSignatureData = getSignatureData(selectedForm, 'final');
    
    // Debug signature data
    console.log('Selected form:', selectedForm);
    console.log('Signatures:', {
      consent: consentSignatureData ? 'Present (length: ' + consentSignatureData.length + ')' : 'Missing',
      cancel: cancelSignatureData ? 'Present (length: ' + cancelSignatureData.length + ')' : 'Missing',
      final: finalSignatureData ? 'Present (length: ' + finalSignatureData.length + ')' : 'Missing'
    });
    
    // For intake forms, show a full preview that matches the client's view
    if (formType === "Intake Form") {
      return (
        <>
          <div
            style={overlayStyle}
            onClick={() => {
              setShowFormPreview(false);
              setSelectedForm(null);
            }}
          />
          <div style={{...modalStyle, width: '90%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3>{formType} - {formatDate(selectedForm.created_at)}</h3>
              <button 
                style={{
                  ...buttonStyle,
                  backgroundColor: '#6c757d'
                }}
                onClick={() => {
                  setShowFormPreview(false);
                  setSelectedForm(null);
                }}
              >
                Close
              </button>
            </div>
            
            {/* Full form preview in iframe to preserve styling */}
            <iframe 
              srcDoc={`
                <html>
                <head>
                  <style>
                    body {
                      font-family: 'Times New Roman', Times, serif;
                      margin: 20px;
                      padding: 20px;
                      line-height: 1.5;
                    }
                    .page {
                      border: 1px solid #000;
                      padding: 40px;
                      max-width: 800px;
                      margin: 0 auto;
                      position: relative;
                    }
                    .header {
                      text-align: center;
                      margin-bottom: 20px;
                    }
                    .footer {
                      text-align: center;
                      margin-top: 40px;
                    }
                    .signature {
                      width: 200px;
                      height: auto;
                    }
                    h2, h3 {
                      text-align: center;
                      margin-bottom: 20px;
                    }
                    .section {
                      margin-top: 40px;
                    }
                    .date-line {
                      display: inline-block;
                      margin-left: 40px;
                    }
                    .client-info {
                      margin-top: 20px;
                      border-top: 1px solid #ccc;
                      padding-top: 20px;
                    }
                    .subsection {
                      margin-top: 30px;
                    }
                    .indent {
                      margin-left: 20px;
                    }
                  </style>
                </head>
                <body>
                  <div class="page">
                    <!-- Header Image -->
                    <div class="header">
                      <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
                    </div>
                    
                    <h2>CONSENT TO TREAT</h2>
                    <p>
                      I, ${clientName}, give my permission and consent to Avenues Unlimited Counseling Center,
                      Inc., to provide me psychotherapeutic treatment to ${patientNames || 'myself'}.
                    </p>
                    <p>
                      While Avenues Unlimited will endeavor to provide standard of care treatment, I fully understand that 
                      because of factors beyond Avenues Unlimited's control, particular outcomes cannot be guaranteed. 
                      Furthermore, I understand that I/he/she/we may experience emotional strains because of the counseling 
                      or therapy, feel worse during the treatment and make life changes which could be difficult.
                    </p>
                    <p>
                      I understand that Avenues Unlimited is not providing emergency services, and I have been informed of whom to call upon in an emergency
                      or during such time as treatment from Avenues Unlimited is unavailable.
                    </p>
                    <p>
                      I understand that regular attendance as recommended by Avenues Unlimited will facilitate maximum therapeutic benefits, but that I/we
                      am/are free to discontinue treatment at any time. If I decide to do so I will notify Avenues Unlimited
                      at least two weeks in advance so that effective planning for continuing care can be implemented.
                    </p>

                    <div>
                      <p>Consent Signature:</p>
                      <img src="${consentSignatureData || (recordData.consentSignatureData ? recordData.consentSignatureData : '')}" class="signature" alt="Consent Signature">
                      <span class="date-line">Date: ${consentDate}</span>
                    </div>

                    <div class="section">
                      <h3>CANCELLATION POLICY</h3>
                      <p>
                        I understand that there is a 24 hour (or 1 working day) rescheduling policy. 
                        A rescheduling charge of the full session fee will result if I do not give notice in this time frame.
                      </p>
                      
                      <p>Cancellation Signature:</p>
                      <img src="${cancelSignatureData || (recordData.cancelSignatureData ? recordData.cancelSignatureData : '')}" class="signature" alt="Cancellation Signature">
                      <span class="date-line">Date: ${cancelDate}</span>
                    </div>

                    <div class="section">
                      <h3>LIMITS OF CONFIDENTIALITY</h3>
                      <p>
                        The contents of counseling, intake and/or assessment sessions are confidential. 
                        Both verbal information and written records about a client cannot be shared with another party without 
                        the written consent of the client or the client's legal guardian, except as required by law. 
                        It is the policy of this center not to release any information about a client without a signed 
                        release of information, or as otherwise required by law. Noted exceptions are as follows:
                      </p>
                      
                      <div class="subsection">
                        <h4>DUTY TO WARN AND PROTECT</h4>
                        <p class="indent">
                          When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law 
                          to warn the intended victim and report identifying information to legal authorities. In cases in which 
                          the client discloses or implies a plan for suicide, Avenues Unlimited is required to notify legal 
                          authorities and make reasonable attempts to notify the family of the client.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>ABUSE OF CHILDREN AND VULNERABLE ADULTS</h4>
                        <p class="indent">
                          If a client discloses or suggests that he or she is abusing a child (or vulnerable adult) or has recently 
                          abused a child (or vulnerable adult); or a child (or vulnerable adult) is in danger of abuse, 
                          Avenues Unlimited is required to report this information to the appropriate social service and/or legal authorities.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>PRENATAL EXPOSURE TO CONTROLLED SUBSTANCES</h4>
                        <p class="indent">
                          Avenues Unlimited is required by law to report admitted prenatal exposure to controlled substances 
                          that are potentially harmful.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>IN THE EVENT OF A CLIENT'S DEATH</h4>
                        <p class="indent">
                          In the event of a client's death, the spouse or parents of a deceased client have a right to access a client's treatment records.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>PROFESSIONAL MISCONDUCT</h4>
                        <p class="indent">
                          Professional misconduct by a health care professional must be reported by other health care professionals. 
                          In cases in which a professional or legal disciplinary meeting is being held regarding the health care professional's actions, 
                          related records may be released in order to substantiate disciplinary concerns.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>COURT ORDERS</h4>
                        <p class="indent">
                          Health care professionals are required to release records of clients when a court order has been entered.
                        </p>
                      </div>
                      
                      <div class="subsection">
                        <h4>MINORS/GUARDIANSHIP</h4>
                        <p class="indent">
                          Parents or legal guardians of non-emancipated minor clients have the right to access the client's records.
                        </p>
                      </div>
                      
                      <p>
                        I have been informed of the limits of confidentiality. Initials: <strong>${initials}</strong>
                      </p>
                    </div>
                    
                    <div class="section">
                      <h3>ACKNOWLEDGMENT</h3>
                      <p>
                        By signing below, I acknowledge that I have read, understood, and agree to all the terms 
                        and conditions outlined in this consent form. I have been given the opportunity to ask questions
                        about the content of this form, and I understand my rights and responsibilities as a client at
                        Avenues Unlimited Counseling Center.
                      </p>
                      
                      <p>Signature:</p>
                      <img src="${finalSignatureData || (recordData.finalSignatureData ? recordData.finalSignatureData : '')}" class="signature" alt="Final Acknowledgment Signature">
                      <span class="date-line">Date: ${consentDate}</span>
                    </div>

                    <div class="client-info">
                      <h3>Client Information</h3>
                      ${diagnosisCode ? `<p><strong>Diagnosis Code:</strong> ${diagnosisCode}</p>` : ''}
                      ${clientPhone ? `<p><strong>Phone:</strong> ${clientPhone}</p>` : ''}
                      ${clientEmail ? `<p><strong>Email:</strong> ${clientEmail}</p>` : ''}
                      ${billingAddress || billingCity || billingState || billingZipCode ? 
                        `<p><strong>Address:</strong> ${billingAddress}${billingAddress && (billingCity || billingState || billingZipCode) ? ', ' : ''}
                        ${billingCity}${billingCity && (billingState || billingZipCode) ? ', ' : ''}
                        ${billingState} ${billingZipCode}</p>` : ''}
                    </div>

                    <!-- Footer Image -->
                    <div class="footer">
                      <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
                    </div>
                  </div>
                </body>
                </html>
              `}
              style={{width: '100%', height: '600px', border: '1px solid #ccc'}}
              title="Form Preview"
            />
            
            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '1rem'}}>
              <button 
                onClick={() => downloadFormAsPdf(selectedForm)}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#28a745'
                }}
              >
                Download PDF
              </button>
            </div>
          </div>
        </>
      );
    }
    
    // For other form types, show a simpler preview
    return (
      <>
        <div
          style={overlayStyle}
          onClick={() => {
            setShowFormPreview(false);
            setSelectedForm(null);
          }}
        />
        <div style={modalStyle}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <h3>{formType} - {formatDate(selectedForm.created_at)}</h3>
            <button 
              style={{
                ...buttonStyle,
                backgroundColor: '#6c757d'
              }}
              onClick={() => {
                setShowFormPreview(false);
                setSelectedForm(null);
              }}
            >
              Close
            </button>
          </div>
          
          <div 
            dangerouslySetInnerHTML={{ __html: renderFormDataAsHtml(selectedForm) }}
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '1rem',
              border: '1px solid #eee',
              borderRadius: '4px'
            }}
          />
          
          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '1rem'}}>
            <button 
              onClick={() => downloadFormAsPdf(selectedForm)}
              style={{
                ...buttonStyle,
                backgroundColor: '#28a745'
              }}
            >
              Download PDF
            </button>
          </div>
        </div>
      </>
    );
  }

  // Function to fetch all clients from portal
  const fetchAllPortalClients = async () => {
    try {
      const res = await API.get('/admin/clients');
      setAllClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle client selection
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    
    // Fetch form data for this client
    try {
      const res = await API.get(`/forms/admin/intake-data/${client.id}`);
      setLocalForms(res.data || []);
    } catch (error) {
      console.error('Error fetching client forms:', error);
      setLocalForms([]);
    }
  };

  // Handle opening a form preview
  const handleViewForm = (form) => {
    setSelectedForm(form);
    setShowFormPreview(true);
  };

  // Acuity interaction methods
  const fetchAcuityClients = async () => {
    try {
      const res = await API.get('/acuity/clients');
      setAcuityClients(res.data || []);
      setShowAcuityList(true);
    } catch (error) {
      console.error('Error fetching Acuity clients:', error);
      alert('Error loading Acuity clients. Please try again later.');
    }
  };

  const handleSelectAcuityClient = async (client) => {
    try {
      // Find email and phone from client object
      const email = client.email || '';
      const phone = client.phone || '';
      
      // Fetch this client's form data based on email
      const res = await API.get(`/acuity/forms-by-email/${encodeURIComponent(email)}`);
      setAcuityFormsByEmail(res.data || []);
    } catch (error) {
      console.error('Error fetching Acuity forms:', error);
      setAcuityFormsByEmail([]);
    }
  };

  // Process manual paste data
  const handleManualPasteSubmit = async () => {
    if (!selectedClient) {
      setPasteMessage('Error: Please select a client first');
      return;
    }
    
    if (!manualData.trim()) {
      setPasteMessage('Error: Paste area is empty');
      return;
    }
    
    try {
      // Send the raw pasted data to the backend
      const res = await API.post('/forms/admin/manual-paste', {
        clientId: selectedClient.id,
        rawData: manualData
      });
      
      if (res.data.success) {
        setPasteMessage('Manual data stored successfully!');
        setManualData(''); // Clear the text area
        
        // Refresh local forms
        const formsRes = await API.get(`/forms/admin/intake-data/${selectedClient.id}`);
        setLocalForms(formsRes.data || []);
      } else {
        setPasteMessage(`Error: ${res.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error storing manual data:', error);
      setPasteMessage('Server error. Please try again later.');
    }
  };

  // All client details modal
  const renderAllClientDetails = () => {
    if (!selectedClient) return null;
    
    return (
      <>
        <div
          style={overlayStyle}
          onClick={() => setShowAllDetails(false)}
        />
        <div style={modalStyle}>
          <h3>Complete Client Details</h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <h4>Personal Information</h4>
            <p><strong>Name:</strong> {selectedClient.client_name}</p>
            <p><strong>Email:</strong> {selectedClient.email || 'Not provided'}</p>
            <p><strong>Phone:</strong> {selectedClient.phone || 'Not provided'}</p>
            
            {selectedClient.billing_address && (
              <div>
                <p><strong>Billing Address:</strong></p>
                <p>{selectedClient.billing_address}</p>
                <p>
                  {selectedClient.city}{selectedClient.city && selectedClient.state ? ', ' : ''}
                  {selectedClient.state} {selectedClient.zip_code}
                </p>
              </div>
            )}
            
            {selectedClient.mailing_address && (
              <div>
                <p><strong>Mailing Address:</strong></p>
                <p>{selectedClient.mailing_address}</p>
                <p>
                  {selectedClient.mailing_city}{selectedClient.mailing_city && selectedClient.mailing_state ? ', ' : ''}
                  {selectedClient.mailing_state} {selectedClient.mailing_zip}
                </p>
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <h4>Billing Information</h4>
            <p><strong>Balance:</strong> ${selectedClient.balance || '0.00'}</p>
            <p><strong>Payment Method:</strong> {selectedClient.payment_method || 'Not specified'}</p>
            {selectedClient.card_last4 && (
              <p><strong>Card:</strong> **** **** **** {selectedClient.card_last4}</p>
            )}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <h4>Additional Information</h4>
            <p><strong>Created:</strong> {formatDate(selectedClient.created_at)}</p>
            <p><strong>Last Updated:</strong> {formatDate(selectedClient.updated_at)}</p>
            <p><strong>Notes:</strong> {selectedClient.notes || 'No notes'}</p>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <button 
              style={buttonStyle}
              onClick={() => setShowAllDetails(false)}
            >
              Close
            </button>
          </div>
        </div>
      </>
    );
  };

  // Calculate displayed clients based on search term
  const displayedClients = searchTerm
    ? allClients.filter((client) =>
        client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allClients;

  // -----------------------------------------------------
  // STYLES
  // -----------------------------------------------------
  // basic modal styles
  const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#fff',
    border: '1px solid #ccc',
    padding: '1.5rem',
    borderRadius: '4px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    zIndex: 9999,
    width: '80%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto'
  };
  
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 9998
  };
  
  const cardStyle = {
    padding: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1rem'
  };
  
  const buttonStyle = {
    display: 'inline-block',
    padding: '0.375rem 0.75rem',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    textAlign: 'center',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    cursor: 'pointer',
    borderRadius: '0.25rem',
    backgroundColor: '#2a5885',
    color: 'white',
    border: '1px solid transparent',
    marginRight: '0.5rem'
  };
  
  const formButtonStyle = {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '0.25rem',
    color: '#212529',
    textDecoration: 'none',
    cursor: 'pointer',
    marginBottom: '0.5rem',
    marginRight: '0.5rem'
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Client Profile</h2>
      {!selectedClient && (
        <p style={{ color: 'red' }}>
          ** Select a local client (on the left) before viewing or storing data. **
        </p>
      )}

      {/* LEFT – portal client search —— RIGHT – details */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
        <div>
          <label>Search Client (Portal):</label>
          <br />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Type to search by client name..."
            style={{ width: '220px', padding: '0.5rem', marginTop: '0.5rem' }}
          />
          {selectedClient && (
            <div style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>
              Selected: {selectedClient.client_name}
            </div>
          )}
          <div
            style={{
              border: '1px solid #ccc',
              width: '220px',
              height: '300px',
              overflowY: 'auto',
              padding: '0.5rem',
              marginTop: '0.5rem'
            }}
          >
            {displayedClients.length === 0 ? (
              <div style={{ color: '#999' }}>No clients found</div>
            ) : (
              displayedClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    padding: '0.5rem 0',
                    borderRadius: '3px',
                    backgroundColor: selectedClient && selectedClient.id === client.id ? '#f0f4f8' : 'transparent'
                  }}
                  onClick={() => handleSelectClient(client)}
                >
                  {client.client_name}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ flex: '1' }}>
          {selectedClient ? (
            <div style={cardStyle}>
              <h3>Client Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                <div>
                  <p><strong>Name:</strong> {selectedClient.client_name}</p>
                  {selectedClient.phone && (
                    <p><strong>Phone:</strong> {selectedClient.phone}</p>
                  )}
                  {selectedClient.email && (
                    <p><strong>Email:</strong> {selectedClient.email}</p>
                  )}
                </div>
                <div>
                  {selectedClient.billing_address && (
                    <p><strong>Address:</strong> {selectedClient.billing_address}</p>
                  )}
                  {(selectedClient.city || selectedClient.state || selectedClient.zip_code) && (
                    <p>
                      {selectedClient.city}{selectedClient.city && selectedClient.state ? ', ' : ''}
                      {selectedClient.state} {selectedClient.zip_code}
                    </p>
                  )}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button 
                  style={buttonStyle}
                  onClick={() => setShowAllDetails(true)}
                >
                  View All Details
                </button>
                <button
                  style={buttonStyle}
                  onClick={() => setShowAcuityModal(true)}
                >
                  Acuity Clients / Forms
                </button>
                <button
                  style={buttonStyle}
                  onClick={() => setShowManualModal(true)}
                >
                  Manual Paste Tool
                </button>
              </div>
            </div>
          ) : (
            <p>Select a client to view details.</p>
          )}
        </div>
      </div>

      {/* LOCAL INTAKE RECORDS */}
      {selectedClient && (
        <div style={{ marginTop: '2rem', ...cardStyle }}>
          <h3>Client Forms</h3>
          {localForms.length === 0 ? (
            <p>No forms found for this client</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {localForms.map((form) => {
                const formType = determineFormType(form);
                const formDate = formatDate(form.created_at);
                
                return (
                  <button
                    key={form.id}
                    style={formButtonStyle}
                    onClick={() => handleViewForm(form)}
                  >
                    {formType} - {formDate}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAllDetails && renderAllClientDetails()}
      {showFormPreview && renderFormPreview()}

      {/* ACUITY modal */}
      {showAcuityModal && (
        <>
          <div
            style={overlayStyle}
            onClick={() => setShowAcuityModal(false)}
          />
          <div style={modalStyle}>
            <h3>Acuity Clients / Forms</h3>
            {!showAcuityList && (
              <button 
                style={buttonStyle}
                onClick={fetchAcuityClients}
              >
                Load Acuity Clients
              </button>
            )}

            {showAcuityList && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label>Search Acuity Clients:</label>
                  <br />
                  <input
                    type="text"
                    value={acuitySearch}
                    onChange={(e) => setAcuitySearch(e.target.value)}
                    placeholder="Type name/email..."
                    style={{ width: '200px', padding: '0.5rem', marginTop: '0.5rem' }}
                  />
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    width: '100%',
                    height: '250px',
                    overflowY: 'auto',
                    padding: '0.5rem'
                  }}
                >
                  {acuityClients
                    .filter((cli) => {
                      const text = acuitySearch.toLowerCase();
                      return (
                        (cli.firstName || '').toLowerCase().includes(text) ||
                        (cli.lastName || '').toLowerCase().includes(text) ||
                        (cli.email || '').toLowerCase().includes(text)
                      );
                    })
                    .map((cli, idx) => (
                      <div
                        key={cli.email + idx}
                        style={{
                          borderBottom: '1px solid #eee',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          borderRadius: '3px',
                          margin: '0.25rem 0'
                        }}
                        onClick={() => handleSelectAcuityClient(cli)}
                      >
                        <strong>
                          {cli.firstName} {cli.lastName}
                        </strong>
                        <br />
                        Email: {cli.email}
                        {cli.phone && <div>Phone: {cli.phone}</div>}
                      </div>
                    ))}
                </div>
              </>
            )}

            {acuityFormsByEmail.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4>Forms Found:</h4>
                {acuityFormsByEmail.map((appt) => {
                  const parsed = parseAcuityFormAnswers(appt.form_answers);
                  if (parsed.length === 0) return null; // skip if no answers
                  return (
                    <div
                      key={appt.id}
                      style={{
                        marginBottom: '1rem',
                        borderBottom: '1px solid #ccc',
                        paddingBottom: '0.5rem'
                      }}
                    >
                      <strong>Form Answers:</strong>
                      {renderFormAnswers(appt.form_answers, appt)}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button 
                style={buttonStyle}
                onClick={() => setShowAcuityModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}

      {/* MANUAL PASTE modal */}
      {showManualModal && (
        <>
          <div
            style={overlayStyle}
            onClick={() => setShowManualModal(false)}
          />
          <div style={modalStyle}>
            <h3>Manual Paste Tool</h3>
            <textarea
              value={manualData}
              onChange={(e) => setManualData(e.target.value)}
              style={{ width: '100%', height: '150px', padding: '0.5rem', marginBottom: '1rem' }}
              placeholder="Paste client data here..."
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button 
                style={buttonStyle}
                onClick={handleManualPasteSubmit}
              >
                Submit Manual Intake
              </button>
              {pasteMessage && (
                <p style={{ 
                  color: pasteMessage.includes('Error') ? 'red' : 'green',
                  margin: '0'
                }}>
                  {pasteMessage}
                </p>
              )}
            </div>

            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button 
                style={{
                  ...buttonStyle,
                  backgroundColor: '#6c757d'
                }}
                onClick={() => setShowManualModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminClientProfilePage;